import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, animate, useDragControls } from "framer-motion";
import { Plus } from "lucide-react";

export default function BottomSheet({ title, count, canAdd, onAdd, children }) {
  const [vh, setVh] = useState(typeof window !== "undefined" ? window.innerHeight : 800);

  useEffect(() => {
    const h = () => setVh(window.innerHeight);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  const SHEET_H = Math.round(vh * 0.9);
  const PEEK = 118;
  const snaps = {
    full: 10,
    half: Math.round(SHEET_H * 0.5),
    collapsed: SHEET_H - PEEK,
  };

  const y = useMotionValue(snaps.half);
  const controls = useDragControls();
  const moved = useRef(false);

  useEffect(() => {
    animate(y, snaps.half, { type: "spring", stiffness: 300, damping: 32 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vh]);

  const snapTo = (v) => animate(y, v, { type: "spring", stiffness: 320, damping: 34 });

  const handleDragEnd = (_e, info) => {
    const cur = y.get();
    const vel = info.velocity.y;
    const arr = [snaps.full, snaps.half, snaps.collapsed];
    let target;
    if (vel < -500) target = snaps.full;
    else if (vel > 500) target = snaps.collapsed;
    else target = arr.reduce((a, b) => (Math.abs(b - cur) < Math.abs(a - cur) ? b : a));
    snapTo(target);
  };

  const toggle = () => {
    if (moved.current) return;
    const cur = y.get();
    snapTo(cur <= snaps.half + 20 ? snaps.collapsed : snaps.full);
  };

  return (
    <motion.div
      data-testid="bottom-sheet"
      className="fixed left-0 right-0 bottom-0 z-30 mx-auto w-full max-w-md bg-white rounded-t-3xl border border-slate-200/70 shadow-[0_-8px_40px_rgba(15,23,42,0.16)] flex flex-col"
      style={{ height: SHEET_H, y }}
      drag="y"
      dragListener={false}
      dragControls={controls}
      dragConstraints={{ top: snaps.full, bottom: snaps.collapsed }}
      dragElastic={0.05}
      onDrag={(_e, info) => {
        if (Math.abs(info.offset.y) > 6) moved.current = true;
      }}
      onDragEnd={handleDragEnd}
    >
      <div
        className="pt-3 pb-3 px-5 shrink-0 cursor-grab active:cursor-grabbing touch-none select-none"
        onPointerDown={(e) => {
          moved.current = false;
          controls.start(e);
        }}
        onClick={toggle}
        data-testid="sheet-handle"
      >
        <div className="mx-auto h-1.5 w-11 rounded-full bg-slate-300 mb-3" />
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-display text-xl font-bold tracking-tight text-slate-900 truncate">
              {title}
            </h2>
            <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-400">
              {count} sichtbar
            </p>
          </div>
          {canAdd && (
            <button
              data-testid="add-item-button"
              onClick={(e) => {
                e.stopPropagation();
                onAdd();
              }}
              className="grid place-items-center h-11 w-11 shrink-0 rounded-2xl bg-primary text-white shadow-lg shadow-amber-500/30 transition-transform hover:scale-[1.05] active:scale-[0.95]"
              aria-label="Objekt hinzufügen"
            >
              <Plus size={22} strokeWidth={2.6} />
            </button>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto overscroll-y-contain px-3 pb-10">{children}</div>
    </motion.div>
  );
}
